import React, { useMemo } from "react";
import { RfiActionToastProvider } from "../../contexts/RfiActionToastContext";
import CustomTable from "../CustomTable";
import RfiTypeBadge from "./RfiTypeBadge";
import RfiTemplateServiceBadges from "./RfiTemplateServiceBadges";
import { MenuDropdownAccordingToStatuses } from "../RFIsComponents/MenuDropdownAccordingToStatuses";
import GridStatusDotCell from "../GridStatusDotCell";
import { mapMyRfiItemToGridRow } from "../../utils/myRfiGridRowMapper";
import { resolveRfiListRowStatus } from "../../utils/rfiRequestActionPermissions";
import {
  COMPACT_GRID_COL_WIDTH_PCT,
  compactGridCycleCellClassName,
  compactGridDateCellClassName,
  compactGridStatusCellClassName,
  getRfiGridCellPaddingClass,
  SIZE_TABLE_COL_MIN,
  SIZE_TABLE_TEXT_COL_MAX,
} from "./rfiGridTableStyles";

/**
 * Intermediate table (similar pattern to UsersListTable): wraps CustomTable with
 * Innovation Lab styling (header/body padding, divides) and RFI grid columns/cells.
 *
 * The parent page owns data, filters, sorting (sortMyRfiItems), and navigation.
 */
export default function RfiGridTable({
  data,
  sortConfig,
  onSort,
  onRowClick,
  refetch,
  isSearchActive = false,
  emptyMessage = "No VQQs found",
  emptyDescription = "No VQQ requests are available at this time.",
  className = "py-0 sm:py-0",
  rowKey = "questionnaire_id",
}) {
  const columns = useMemo(
    () => [
      {
        key: "vendor",
        header: "Vendor",
        sortable: true,
        width: "10%",
        minWidth: SIZE_TABLE_COL_MIN.org,
        contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
        cellClassName: "text-gray-900 font-medium",
        render: (item) => {
          const row = mapMyRfiItemToGridRow(item);
          return <span className="break-words">{row.vendor}</span>;
        },
      },
      {
        key: "type",
        header: "Type",
        sortable: false,
        width: "8%",
        minWidth: SIZE_TABLE_COL_MIN.type,
        render: (item) => <RfiTypeBadge item={item} />,
      },
      // TODO: uncomment for enable GXP column      
      // {
      //   key: "gxp",
      //   header: "GxP",
      //   sortable: true,
      //   width: "6%",
      //   render: (item) => {
      //     const row = mapMyRfiItemToGridRow(item);
      //     return row.gxp ? (
      //       <span className="inline-flex items-center rounded-md bg-slate-500 px-2 py-1 text-xs font-medium text-white">
      //         {row.gxp}
      //       </span>
      //     ) : (
      //       <span className="text-gray-400">--</span>
      //     );
      //   },
      // },
      {
        key: "template",
        header: "Template",
        sortable: true,
        width: "12%",
        minWidth: SIZE_TABLE_COL_MIN.template,
        contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
        render: (item) => (
          <RfiTemplateServiceBadges
            services={item?.services ?? item?.Services}
            fallbackTemplateName={item?.name}
          />
        ),
      },
      {
        key: "location",
        header: "Location",
        sortable: true,
        width: "10%",
        minWidth: SIZE_TABLE_COL_MIN.location,
        contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
        cellClassName: "text-gray-900",
        render: (item) => {
          const row = mapMyRfiItemToGridRow(item);
          return row.location ? (
            <span className="break-words">{row.location}</span>
          ) : (
            <span className="text-gray-400">--</span>
          );
        },
      },
      // TODO: uncomment for enable risk column
      // {
      //   key: "risk",
      //   header: "Risk Summary",
      //   sortable: true,
      //   width: "10%",
      //   cellClassName: "text-gray-800",
      //   render: (item) => {
      //     const row = mapMyRfiItemToGridRow(item);
      //     return row.riskSummary ? (
      //       row.riskSummary
      //     ) : (
      //       <span className="text-gray-400">--</span>
      //     );
      //   },
      // },
      {
        key: "status",
        header: "Status",
        sortable: true,
        width: COMPACT_GRID_COL_WIDTH_PCT,
        minWidth: SIZE_TABLE_COL_MIN.status,
        cellClassName: compactGridStatusCellClassName,
        render: (item) => (
          <GridStatusDotCell status={resolveRfiListRowStatus(item)} />
        ),
      },
      {
        key: "requestDate",
        header: "Request Date",
        sortable: true,
        width: COMPACT_GRID_COL_WIDTH_PCT,
        minWidth: SIZE_TABLE_COL_MIN.date,
        cellClassName: compactGridDateCellClassName,
        headerClassName: "text-center [&>div]:justify-center",
        render: (item) => {
          const row = mapMyRfiItemToGridRow(item);
          return row.requestDateDisplay || (
            <span className="text-gray-400">--</span>
          );
        },
      },
      {
        key: "dueDate",
        header: "Due Date",
        sortable: true,
        width: COMPACT_GRID_COL_WIDTH_PCT,
        minWidth: SIZE_TABLE_COL_MIN.date,
        cellClassName: compactGridDateCellClassName,
        headerClassName: "text-center [&>div]:justify-center",
        render: (item) => {
          const row = mapMyRfiItemToGridRow(item);
          return row.dueDateDisplay || (
            <span className="text-gray-400">--</span>
          );
        },
      },
      {
        key: "releasedDate",
        header: "Release Date",
        sortable: true,
        width: COMPACT_GRID_COL_WIDTH_PCT,
        minWidth: SIZE_TABLE_COL_MIN.date,
        cellClassName: compactGridDateCellClassName,
        headerClassName: "text-center [&>div]:justify-center",
        render: (item) => {
          const row = mapMyRfiItemToGridRow(item);
          return row.initialReleaseDateDisplay || (
            <span className="text-gray-400">--</span>
          );
        },
      },
      {
        key: "completionDate",
        header: "Completion Date",
        sortable: true,
        width: COMPACT_GRID_COL_WIDTH_PCT,
        minWidth: SIZE_TABLE_COL_MIN.date,
        cellClassName: compactGridDateCellClassName,
        headerClassName: "text-center [&>div]:justify-center",
        render: (item) => {
          const row = mapMyRfiItemToGridRow(item);
          return row.completionDateDisplay || (
            <span className="text-gray-400">--</span>
          );
        },
      },
      {
        key: "cycleTime",
        header: "Cycle Time",
        sortable: true,
        width: COMPACT_GRID_COL_WIDTH_PCT,
        minWidth: SIZE_TABLE_COL_MIN.cycle,
        cellClassName: compactGridCycleCellClassName,
        headerClassName: "text-center [&>div]:justify-center",
        render: (item) => {
          const row = mapMyRfiItemToGridRow(item);
          return row.cycleTime || <span className="text-gray-400">--</span>;
        },
      },
      {
        key: "actions",
        header: "",
        sortable: false,
        width: "4%",
        minWidth: SIZE_TABLE_COL_MIN.actions,
        align: "right",
        cellClassName: "text-right",
        excludeFromRowClassName: true,
        render: (item) => (
          <div onClick={(e) => e.stopPropagation()}>
            <MenuDropdownAccordingToStatuses
              status={resolveRfiListRowStatus(item)}
              type="RFI"
              isMyRFI={true}
              isDocLibrary={false}
              questionnaireId={item?.questionnaire_id}
              details={item}
              isLegacyRfi={item?.v1}
              v1_doc_id={item?.mongo_sponsor_rfiresponse_id}
              v1_doc_name={item?.v1_document_name}
              refetch={refetch}
            />
          </div>
        ),
      },
    ],
    [refetch]
  );

  return (
    <RfiActionToastProvider>
    <div className="w-full min-w-0 max-w-full">
      <CustomTable
        columns={columns}
        data={data}
        sortConfig={sortConfig}
        onSort={onSort}
        fixedColumnWidths
        emptyMessage={emptyMessage}
        emptyDescription={emptyDescription}
        isSearchActive={isSearchActive}
        edgeToEdge
        className={className}
        rowKey={rowKey}
        onRowClick={onRowClick}
        getHeaderCellPaddingClass={(colIndex, totalCols) =>
          getRfiGridCellPaddingClass(colIndex, totalCols, true)
        }
        getBodyCellPaddingClass={(colIndex, totalCols) =>
          getRfiGridCellPaddingClass(colIndex, totalCols, false)
        }
        scrollWrapperClassName="bg-white"
        tableDivideClassName="divide-gray-200"
        theadClassName="bg-gray-50 [&_th]:align-middle [&_th]:min-h-[3rem]"
        tbodyClassName="divide-y divide-gray-200 bg-white"
        useDividedBodyRows
      />
    </div>
    </RfiActionToastProvider>
  );
}
